import { offsetDateForServer } from '../../../shared/timeZoneLogic';
import { liveWindows } from './presaleWindows';

export const PRICE_OPTION_STARTERS = ['Adult', 'Child', 'Senior', 'Student'];

const SECTION_ADULT = / - Adult$/;

export const sectionIsAdult = (ticketType) => SECTION_ADULT.test(ticketType.base_name || '');

export const sectionName = (ticketType) => (ticketType.base_name || '').replace(SECTION_ADULT, '');

export const PRICE_OPTION_MODES = [
  { value: '', label: 'Fixed price' },
  { value: 'amount_off', label: '$ off the section price' },
  { value: 'percent_off', label: '% off the section price' },
];

export const showsPriceOptions = (ticketType, seated, seatedShow) => Boolean(
  ticketType.id && !ticketType.price_parent_id && !ticketType.presale_parent_id
  && (seated || (ticketType.manifested && !seatedShow && liveWindows(ticketType).length === 0))
);

export const priceOptionKey = (option) => (option.id != null ? option.id : option.key);

export const livePriceOptions = (ticketType) => (ticketType.price_options || []).filter((option) => !option._destroy);

export const priceOptionLabel = (name, sectionName) => {
  const prefix = `${sectionName} - `;
  return (name || '').startsWith(prefix) ? name.slice(prefix.length) : (name || '');
};

export const priceOptionsForForm = (data) => (data.price_options || []).map((option) => ({
  ...option,
  label: priceOptionLabel(option.base_name, sectionName(data)),
  maximum_tickets_per_order: option.maximum_tickets_per_order == null || option.maximum_tickets_per_order === data.maximum_tickets_per_order
    ? ''
    : String(option.maximum_tickets_per_order),
  price_offset_kind: option.price_offset_kind || '',
  price_offset: option.price_offset == null ? '' : String(parseFloat(option.price_offset)),
}));

export const buildPriceOption = (label) => ({
  key: `new-${Date.now()}-${Math.random()}`,
  newRecord: true,
  label,
  base_price_two_decimals: '',
  description: '',
  maximum_tickets_per_order: '',
  price_offset_kind: '',
  price_offset: '',
});

const normalized = (label) => (label || '').trim().toLowerCase();

export const amount = (value) => parseFloat(String(value).split(',').join(''));

export const priceAfterOffset = (ticketType, option) => {
  const sectionCents = Math.round(amount(ticketType.base_price_two_decimals) * 100);
  const offset = amount(option.price_offset);
  const cents = option.price_offset_kind === 'percent_off' ? Math.round((sectionCents * (100 - offset)) / 100) : sectionCents - Math.round(offset * 100);
  return cents / 100;
};

const offsetError = (ticketType, option) => {
  if (!Number.isFinite(amount(ticketType.base_price_two_decimals))) return "Set the section's price first";
  const offset = amount(option.price_offset);
  if (!(offset > 0)) return 'Set an amount off';
  if (option.price_offset_kind === 'percent_off' && offset >= 100) return 'Must be less than 100%';
  if (option.price_offset_kind === 'amount_off' && offset >= amount(ticketType.base_price_two_decimals)) return "Must be less than the section's price";
  return null;
};

export const priceOptionNameError = (ticketType, option) => {
  if (!normalized(option.label)) return 'Name this price';
  const sameName = livePriceOptions(ticketType).filter((other) => normalized(other.label) === normalized(option.label));
  if (sameName.length > 1) return 'Each price in a section needs its own name';
  if (normalized(option.label) === 'adult' && sectionIsAdult(ticketType)) return 'The section price is already Adult';
  return null;
};

export const priceOptionPriceError = (ticketType, option) => {
  if (option.price_offset_kind) return offsetError(ticketType, option);
  if (option.base_price_two_decimals === '' || option.base_price_two_decimals == null) return 'Set a price';
  return null;
};

export const priceOptionError = (ticketType, option) => priceOptionNameError(ticketType, option) || priceOptionPriceError(ticketType, option);

export const priceOptionsValid = (ticketType) => livePriceOptions(ticketType).every((option) => !priceOptionError(ticketType, option));

export const priceOptionsAttributes = (ticketType) => {
  const options = (ticketType.price_options || []).filter((option) => !(option.newRecord && option._destroy));
  if (options.length === 0) return undefined;

  return options.map((option) => ({
    id: option.newRecord ? '' : option.id,
    name: `${sectionName(ticketType)} - ${option.label.trim()}`,
    price: option.price_offset_kind ? null : amount(option.base_price_two_decimals),
    settlement_amount: option.price_offset_kind ? null : amount(option.base_price_two_decimals),
    price_offset_kind: option.price_offset_kind || '',
    price_offset: option.price_offset_kind ? amount(option.price_offset) : '',
    description: option.description,
    maximum_tickets_per_order: amount(option.maximum_tickets_per_order === '' ? ticketType.maximum_tickets_per_order : option.maximum_tickets_per_order),
    availability: ticketType.availability,
    start_show_activity_id: ticketType.start_show_activity_id,
    end_show_activity_id: ticketType.end_show_activity_id,
    start_time: offsetDateForServer(ticketType.start_time),
    end_time: offsetDateForServer(ticketType.end_time),
    wait_for_ticket_type_id: ticketType.wait_for_ticket_type_id,
    _destroy: option._destroy ? 'true' : '',
  }));
};
